import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaBroom, FaEdit, FaEye, FaPlus, FaSearch } from 'react-icons/fa';
import PageContainer from '../../components/geral/PageContainer';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import { useNota } from '../../contexts/vendas/NotaContext';
import { useFornecedor } from '../../contexts/pessoas/FornecedorContext';
import { useNatureza } from '../../contexts/vendas/NaturezaContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { formatCurrency } from '../../utils/numberUtils';
import ViewEntradaNotaModal from './ViewEntradaNotaModal';
import './ConsultaEntradaNotaPage.css';

const dataLocal = (data) => `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
const filtrosPadrao = () => {
  const hoje = new Date();
  return { dataLancamentoInicial: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), dataLancamentoFinal: dataLocal(hoje), fornecedorId: '', naturezaId: '', numeroDocumento: '', modeloNF: '', chave: '', situacao: '0', ordenarPor: 'dataLancamento', direcao: 'desc' };
};

const lerFiltros = (params) => {
  const padrao = filtrosPadrao();
  Object.keys(padrao).forEach((campo) => { if (params.has(campo)) padrao[campo] = params.get(campo); });
  return padrao;
};

const situacaoNota = (nota) => {
  if (nota.exclusaoData) return { texto: 'Excluída', classe: 'excluida' };
  if (Number(nota.situacaoNF) === 4) return { texto: 'Cancelada', classe: 'cancelada' };
  return { texto: 'Ativa', classe: 'ativa' };
};

const ConsultaEntradaNotaPage = () => {
  const { consultarEntradas, obterEntrada } = useNota();
  const { fornecedores } = useFornecedor();
  const { naturezas } = useNatureza();
  const { showMessage } = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryAtual = searchParams.toString();
  const [filtros, setFiltros] = useState(() => lerFiltros(searchParams));
  const [resultado, setResultado] = useState({ itens: [], totalRegistros: 0, valorTotal: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ show: false, entrada: null, loading: false, error: '' });
  const naturezasEntrada = useMemo(() => naturezas.filter((item) => Number(item.tipoMov) === 1), [naturezas]);

  useEffect(() => setFiltros(lerFiltros(searchParams)), [queryAtual]);

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      setResultado(await consultarEntradas({
        ...lerFiltros(new URLSearchParams(queryAtual)),
        tamanhoPagina: 0,
      }));
    } catch (error) {
      showMessage(error?.message || 'Não foi possível consultar as entradas.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [consultarEntradas, queryAtual, showMessage]);

  useEffect(() => { buscar(); }, [buscar]);

  const aplicarFiltros = (proximos) => {
    const params = new URLSearchParams();
    Object.entries(proximos).forEach(([campo, valor]) => {
      if (valor !== '' && valor !== null && valor !== undefined) params.set(campo, valor);
    });
    setSearchParams(params);
  };

  const visualizar = async (id) => {
    setModal({ show: true, entrada: null, loading: true, error: '' });
    try {
      const entrada = await obterEntrada(id);
      setModal({ show: true, entrada, loading: false, error: '' });
    } catch (error) {
      setModal({ show: true, entrada: null, loading: false, error: error?.message || 'Não foi possível carregar a entrada.' });
    }
  };

  const editar = (id) => {
    const retorno = `${location.pathname}${location.search}`;
    navigate(`/entrada/${id}?retorno=${encodeURIComponent(retorno)}`);
  };

  const mudarOrdenacao = (campo) => {
    const direcao = filtros.ordenarPor === campo && filtros.direcao === 'asc' ? 'desc' : 'asc';
    aplicarFiltros({ ...filtros, ordenarPor: campo, direcao });
  };

  return <PageContainer>
    <div className="consulta-entrada-page">
      <header className="consulta-entrada-header">
        <div><h2 className="page-title">Consultar entradas</h2><span>Localize notas de compra, confira os itens ou continue uma correção.</span></div>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/entrada')}><FaPlus /> Nova entrada</button>
      </header>
      <PageContent>
        <form className="consulta-entrada-filtros" onSubmit={(e) => { e.preventDefault(); aplicarFiltros(filtros); }}>
          <label>De<input type="date" className="form-control" value={filtros.dataLancamentoInicial} onChange={(e) => setFiltros({ ...filtros, dataLancamentoInicial: e.target.value })} /></label>
          <label>Até<input type="date" className="form-control" value={filtros.dataLancamentoFinal} onChange={(e) => setFiltros({ ...filtros, dataLancamentoFinal: e.target.value })} /></label>
          <label className="fornecedor">Fornecedor<select className="form-select" value={filtros.fornecedorId} onChange={(e) => setFiltros({ ...filtros, fornecedorId: e.target.value })}><option value="">Todos</option>{fornecedores.map((item) => <option key={item.id} value={item.id}>{item.id} - {item.pessoa?.nome}</option>)}</select></label>
          <label className="natureza">Natureza<select className="form-select" value={filtros.naturezaId} onChange={(e) => setFiltros({ ...filtros, naturezaId: e.target.value })}><option value="">Todas</option>{naturezasEntrada.map((item) => <option key={item.id} value={item.id}>{item.id} - {item.descricao}</option>)}</select></label>
          <label>Número<input type="number" className="form-control" value={filtros.numeroDocumento} onChange={(e) => setFiltros({ ...filtros, numeroDocumento: e.target.value })} /></label>
          <label>Modelo<select className="form-select" value={filtros.modeloNF} onChange={(e) => setFiltros({ ...filtros, modeloNF: e.target.value })}><option value="">Todos</option><option value="55">55 - NF-e</option><option value="65">65 - NFC-e</option></select></label>
          <label className="chave">Chave<input className="form-control" maxLength={44} value={filtros.chave} onChange={(e) => setFiltros({ ...filtros, chave: e.target.value.replace(/\D/g, '') })} /></label>
          <label>Situação<select className="form-select" value={filtros.situacao} onChange={(e) => setFiltros({ ...filtros, situacao: e.target.value })}><option value="0">Ativas</option><option value="1">Canceladas</option><option value="2">Excluídas</option><option value="3">Todas</option></select></label>
          <div className="consulta-entrada-filter-actions"><button type="button" className="btn btn-outline-secondary" onClick={() => aplicarFiltros(filtrosPadrao())}><FaBroom /> Limpar</button><button type="submit" className="btn btn-primary"><FaSearch /> Buscar</button></div>
        </form>
        <section className="consulta-entrada-tabela-card">
          <div className="consulta-entrada-table-wrap">
            <table className="page-content-table consulta-entrada-table">
              <thead><tr><th><button onClick={() => mudarOrdenacao('id')}>ID</button></th><th><button onClick={() => mudarOrdenacao('dataLancamento')}>Lançamento</button></th><th>Emissão</th><th><button onClick={() => mudarOrdenacao('fornecedor')}>Fornecedor</button></th><th>Documento</th><th>Natureza</th><th className="text-end">Itens</th><th><button onClick={() => mudarOrdenacao('valorTotal')}>Total</button></th><th>Situação</th><th>Ações</th></tr></thead>
              <tbody>{loading ? <tr><td colSpan="10" className="consulta-entrada-empty"><div className="spinner-border spinner-border-sm text-primary" /> Consultando entradas...</td></tr> : resultado.itens?.length ? resultado.itens.map((nota) => { const status = situacaoNota(nota); return <tr key={nota.id}>
                <td className="fw-semibold">#{nota.id}</td><td>{nota.dataLancamento ? new Date(nota.dataLancamento).toLocaleString('pt-BR') : '—'}</td><td>{nota.dataEmissao ? new Date(nota.dataEmissao).toLocaleDateString('pt-BR') : '—'}</td><td>{nota.fornecedorNome || '—'}</td><td>{nota.modeloNF || '—'} / {nota.serie || '—'} / {nota.numeroDocumento || '—'}</td><td>{nota.naturezaDescricao}</td><td className="text-end">{nota.quantidadeItens}</td><td className="text-end fw-semibold">{formatCurrency(nota.valorTotal)}</td><td><span className={`consulta-entrada-badge ${status.classe}`}>{status.texto}</span></td><td><div className="consulta-entrada-actions"><button type="button" className="btn btn-sm btn-outline-primary" onClick={() => visualizar(nota.id)} title="Visualizar"><FaEye /><span>Visualizar</span></button><button type="button" className="btn btn-sm btn-outline-secondary" disabled={!nota.editavel} onClick={() => editar(nota.id)} title={nota.editavel ? 'Editar' : 'Entrada inativa'}><FaEdit /><span>Editar</span></button></div></td>
              </tr>; }) : <tr><td colSpan="10" className="consulta-entrada-empty">Nenhuma entrada encontrada para os filtros informados.</td></tr>}</tbody>
            </table>
          </div>
        </section>
      </PageContent>
      <PageFooter><div className="consulta-entrada-resumo"><span><strong>{resultado.totalRegistros || 0}</strong> entradas encontradas</span><span>Total filtrado <strong>{formatCurrency(resultado.valorTotal)}</strong></span></div></PageFooter>
      <ViewEntradaNotaModal {...modal} onClose={() => setModal({ show: false, entrada: null, loading: false, error: '' })} onEdit={editar} />
    </div>
  </PageContainer>;
};

export default ConsultaEntradaNotaPage;
